import React, { useEffect } from "react";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Avatar, Box, Stack, Typography } from "@mui/material";
import QrCode2Icon from '@mui/icons-material/QrCode2';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getQrCodeThunk } from "../qrCodeThunk";
import { useDispatch } from "react-redux";
import { QR, QrActionButton } from "./qrUi";

export default function PageButtons({ stats = [], children, onCreate }) {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(getQrCodeThunk());
    }, [dispatch]);


    return (
        <Box
            sx={{
                position: "relative",
                overflow: "hidden",
                p: { xs: 1.5, sm: 2, md: 2.5 },
                borderRadius: 3,
                border: `1px solid ${QR.border}`,
                bgcolor: "#fff", // plain white (no green tint / glow)
                boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
            }}
        >
            {/* Top row: title + Create QR button */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
            {/* Title */}
            <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar
                    variant="rounded"
                    sx={{
                        width: 46,
                        height: 46,
                        borderRadius: 2.5,
                        color: "#fff",
                        background: `linear-gradient(135deg, ${QR.main}, ${QR.light})`,
                        boxShadow: `0 8px 18px -8px ${QR.main}`,
                    }}
                >
                    <QrCode2Icon />
                </Avatar>
                <Box>
                    <Typography variant="h6" fontWeight={800} color="#0f172a" lineHeight={1.2}>
                        QR Codes
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Create WhatsApp QR codes and track the leads they bring in
                    </Typography>
                </Box>
            </Stack>

            {/* Create QR button: opens the create popup (list page passes onCreate) */}
            {onCreate && (
                <QrActionButton
                    variant="solid"
                    icon={<AddCircleOutlineIcon sx={{ fontSize: 17 }} />}
                    label="Create QR"
                    onClick={onCreate}
                />
            )}
            </Box>

            {/* Stats strip inside the header (only when the page passes stats) */}
            {stats.length > 0 && (
                <Box
                    sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: `1px solid ${QR.border}`,
                        display: "grid",
                        gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))", lg: `repeat(${stats.length}, minmax(0, 1fr))` },
                        gap: 1.5,
                    }}
                >
                    {stats.map((s) => (
                        <Stack
                            key={s.label}
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            role={s.onClick ? "button" : undefined}
                            tabIndex={s.onClick ? 0 : undefined}
                            aria-pressed={s.onClick ? Boolean(s.active) : undefined}
                            title={s.onClick ? (s.active ? "Click to show all QR codes" : `Show QR codes: ${s.label}`) : undefined}
                            onClick={s.onClick}
                            onKeyDown={(e) => s.onClick && (e.key === "Enter" || e.key === " ") && (e.preventDefault(), s.onClick())}
                            sx={{
                                position: "relative",
                                p: 1.5,
                                borderRadius: 3,
                                cursor: s.onClick ? "pointer" : "default",
                                userSelect: "none",
                                // Active (filtering) card: tinted background + coloured border
                                bgcolor: s.active ? `${s.color}0d` : "#fff",
                                border: `1px solid ${s.active ? s.color : QR.border}`,
                                borderTop: `3px solid ${s.color}`,
                                boxShadow: s.active ? `0 8px 18px -10px ${s.color}` : "0 1px 3px rgba(16,24,40,0.06)",
                                transition: "transform .15s ease, box-shadow .15s ease, background-color .15s ease",
                                "&:hover": { transform: "translateY(-2px)", boxShadow: `0 8px 18px -10px ${s.color}` },
                                "&:focus-visible": { outline: `2px solid ${s.color}`, outlineOffset: 2 },
                            }}
                        >
                            {s.active && (
                                <CheckCircleIcon sx={{ position: "absolute", top: 8, right: 8, fontSize: 18, color: s.color }} />
                            )}
                            <Avatar variant="rounded" sx={{ width: 40, height: 40, borderRadius: 2, color: s.color, bgcolor: `${s.color}14` }}>
                                {s.icon}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap display="block">
                                    {s.label}
                                </Typography>
                                <Typography variant="h6" fontWeight={800} lineHeight={1.2} color="#0f172a">
                                    {s.value}
                                </Typography>
                            </Box>
                        </Stack>
                    ))}
                </Box>
            )}

            {/* Page content inside the same card (e.g. the QR table) */}
            {children && (
                <Box sx={{ mt: 2.5, pt: 0.5, borderTop: `1px solid ${QR.border}` }}>
                    {children}
                </Box>
            )}
        </Box>
    );
}
